
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, gql, useMutation} from '@apollo/client';
import throttle from 'lodash.throttle';
import {
  Input,
  Divider,
  Accordion,
  Dropdown,
  Button,
  Dimmer,
  Loader,
  Icon,
  Container,
  Header,
  Segment,
  Label,
  Message,
} from 'semantic-ui-react'
import DatePicker from "react-datepicker";

import { PATHS } from '../../../../paths';
import { formatDateTime, formatDateTimeToDay } from "../../../../dates";

import styles from './AdminClassView.module.css';

import { AttendanceGraph } from "../../../../components/Protected/AttendanceGraph";
import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { Session } from '../../../../components/Session';
import { useIsAdmin } from "../../../../hooks/isAdmin";
import { useBlocks } from "../../../../hooks/useBlocks";

import { CourseAdmin } from '../CourseAdmin';
import { CourseTags } from '../CourseTags';
import { CourseNameField } from '../../../../components/CourseNameField';
import { validateCourseName } from '../../../../utils/courseName';

const GET_COURSE = gql`
  query Courses($courseId: Int!) {
    course(courseId: $courseId) {
      id
      name
      canEdit
      SKU
      archivedAt
      attendancePercentage
      teachers {
          id
          name
      }
      courseParticipantCount
      tags {
        id
        name
      }
      futureParticipants {
          id
          name
      }
      sessions {
        id
        course {
            id
            name
            SKU
        }
        durationMinutes
        attendance {
            details
            status
            id
            isTeacher
            user {
                name
                id
                identifier
                photoUrl
            }
        }
        startTime
        attendanceCount
        reasonForCancellation
        attendancePercentage
      }
    }
  }`;

const GET_TEACHER_CANDIDATES = gql`
  query TeacherCandidates {
    teacherCandidates { id name }
  }
`;

const ADD_TEACHER = gql`
  mutation AddTeacherToCourse($courseId: Int!, $userId: Int!) {
    addTeacherToCourse(courseId: $courseId, userId: $userId) { status }
  }
`;

const REMOVE_TEACHER = gql`
  mutation RemoveTeacherFromCourse($courseId: Int!, $userId: Int!) {
    removeTeacherFromCourse(courseId: $courseId, userId: $userId) { status }
  }
`;

const ADD_PERSON_TO_SESSION = gql`
mutation AddPersonToSession($sessionId: Int!, $identifier: String, $status: String) {
  addPersonToSession(sessionId: $sessionId, identifier: $identifier, status: $status) {
    id
  }
}`;

const DELETE_SESSION = gql`
mutation Mutation($sessionId: Int!, $reason: String) {
  cancelSession(sessionId: $sessionId, reason: $reason) {
    id
  }
}`;

const ADD_SESSION_TO_COURSE = gql`
mutation AddSession($courseId: Int!, $date: String) {
  addSession(courseId: $courseId, date: $date) {
    id
  }
}
`;

const UPDATE_SESSION_TIME = gql`
mutation UpdateSessionTime($sessionId: Int!, $magnitude: Int!, $forwardDirection: Boolean!, $unit: String!) {
  updateSessionTime(sessionId: $sessionId, magnitude: $magnitude, forwardDirection: $forwardDirection, unit: $unit) {
    id
  }
}`;

const DUPLICATE_COURSE = gql`
  mutation Mutation($courseToCopyId: Int!, $newCourseName: String!, $daysToCopyItForward: Int!, $newBlockTagId: Int!, $oldBlockTagId: Int!) {
    duplicateCourse(courseToCopyId: $courseToCopyId, newCourseName: $newCourseName, daysToCopyItForward: $daysToCopyItForward, newBlockTagId: $newBlockTagId, oldBlockTagId: $oldBlockTagId) {
      status
    }
  }`;

const DELETE_COURSE = gql`
  mutation Mutation($courseId: Int!) {
    deleteCourse(courseId: $courseId) {
      status
    }
  }`;

const ARCHIVE_COURSE = gql`
  mutation ArchiveCourse($courseId: Int!) {
    archiveCourse(courseId: $courseId) {
      id
      archivedAt
    }
  }`;

const RENAME_COURSE = gql`
  mutation RenameCourse($courseId: Int!, $newCourseName: String!) {
    renameCourse(courseId: $courseId, newCourseName: $newCourseName) {
      status
    }
  }`

const REMOVE_FUTURE_TEACHER_ATTENDANCE = gql`
  mutation RemoveFutureTeacherAttendance($courseId: Int!) {
    removeFutureTeacherAttendance(courseId: $courseId) {
      status
    }
  }`

const ADD_FUTURE_TEACHER_ATTENDANCE = gql`
  mutation AddFutureTeacherAttendance($courseId: Int!) {
    addFutureTeacherAttendance(courseId: $courseId) {
      status
    }
  }`


const CANCELLATION_REASON = [
  {
    key: 'Teacher-sick',
    text: 'Teacher sick',
    value:'Teacher sick',
  },
  {
    key: 'Teacher-holiday',
    text: 'Teacher holiday',
    value:'Teacher holiday',
  },
  {
    key: 'Company-cancelled',
    text: 'Company cancelled',
    value:'Company cancelled',
  },
  {
    key: 'Other',
    text: 'Other',
    value:'Other',
  }
]

const AdminClassView = () => {
  let navigate = useNavigate();
  let { id } = useParams();
  const isAdmin = useIsAdmin();
  const { currentBlockTagId, nextBlockTagId } = useBlocks();

  const [disableDuplicate, setDisableDuplicate] = useState(false);
  const [newCourseNameDuplicate, setNewCourseNameDuplicate] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [daysToCopyItForward, setDaysToCopyItForward] = useState(0);
  const { loading, error, data, refetch } = useQuery(GET_COURSE, { variables: { courseId: Number(id) } });

  const { data: teacherCandidateData } = useQuery(GET_TEACHER_CANDIDATES);

  const [addTeacherToCourse] = useMutation(ADD_TEACHER);

  const [removeTeacherFromCourse] = useMutation(REMOVE_TEACHER);

  const [teacherBusy, setTeacherBusy] = useState(false);

  const [teacherError, setTeacherError] = useState<string | null>(null);


  // Both mutations are roster operations: adding restores an archived assignment rather

  // than making a second one, and removing archives rather than deletes, so the class

  // keeps showing who taught it. Refetch rather than patch the cache — removing a teacher

  // also clears their future attendance rows, which this query reads.

  const changeTeacher = async (userId: number, add: boolean) => {

    setTeacherBusy(true);

    setTeacherError(null);

    try {

      const mutate = add ? addTeacherToCourse : removeTeacherFromCourse;

      await mutate({ variables: { courseId: Number(id), userId } });

      await refetch({ variables: { courseId: Number(id) } });

    } catch (err) {

      setTeacherError(err instanceof Error ? err.message : 'That did not work');

    } finally {

      setTeacherBusy(false);

    }

  };


  const [addPersonToSession] = useMutation(ADD_PERSON_TO_SESSION);
  const [deleteSession] = useMutation(DELETE_SESSION);

  const [newSessionDate, setNewSessionDate] = useState<Date | null>(new Date());
  const [addSessionToCourse] = useMutation(ADD_SESSION_TO_COURSE);
  const [updateSessionTime] = useMutation(UPDATE_SESSION_TIME);
  const [duplicateClass] = useMutation(DUPLICATE_COURSE);
  const [renameCourse] = useMutation(RENAME_COURSE);
  const [deleteCourse] = useMutation(DELETE_COURSE);
  const [archiveCourse] = useMutation(ARCHIVE_COURSE);
  const [removeFutureTeacherAttendance] = useMutation(REMOVE_FUTURE_TEACHER_ATTENDANCE);
  const [addFutureTeacherAttendance] = useMutation(ADD_FUTURE_TEACHER_ATTENDANCE);

  const [editMode, setEditMode] = useState(false);
  const [activeId, setActiveId] = useState();
  const [identifier, setIdentifier] = useState('');
  const [hasUsedAccordian, setHasUsedAccordian] = useState(false);

  const TenHoursAgo = Number(new Date()) - 10*60*60*1000;

  // A bad name is how a bad name propagates block to block, so duplication waits for a good one.
  const duplicateNameIsValid = validateCourseName(newCourseNameDuplicate).valid;

  // The last name we actually sent, so the throttled as-you-type save and an accepted
  // suggestion cannot both fire the same rename.
  const lastSavedName = useRef<string | null>(null);

  const saveCourseName = useCallback(async (newName: string) => {
      // The as-you-type path calls this mid-typing, so a half-typed name would reach the server
      // and be rejected. Only a valid name is worth saving.
      if (!validateCourseName(newName).valid || lastSavedName.current === newName) {
        return;
      }
      lastSavedName.current = newName;
      await renameCourse({ variables: { courseId: Number(id), newCourseName: newName } })
      refetch();
  }, [renameCourse, id, refetch]);

  const updateCourseName = useCallback(throttle((newName: string) => {
      saveCourseName(newName);
  }, 500, { leading: false, trailing: true }), [saveCourseName]);

  useEffect(() => {
    if (!activeId && !loading && !error && data != null && data.course) {
      const nextSession = data.course.sessions.find(
        (s: {startTime:number, reasonForCancellation: string | null}) =>
              s.startTime > TenHoursAgo
          && (s.reasonForCancellation == null || s.reasonForCancellation === ""))

      if (nextSession != null && hasUsedAccordian === false) {
        setActiveId(nextSession.id)
      }
    }
  }, [loading, error, data, activeId, hasUsedAccordian])

  if (loading) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
            <Dimmer active inverted>
              <Loader inverted content='Loading' />
            </Dimmer>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  // check for no internet connection
  if (error && error.message === 'Failed to fetch') {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                No internet connection
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (!data || !data.course) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                Class not found
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (error) {
    return <div />
  }

  // TODO: move Info section to its own component
  return (
    <ResponsiveContainer>
      <Segment key={id} style={{ padding: '2em 0em' }} vertical>
        <Container>
          <div className={styles.backRow}>
            <Icon name='arrow left' size='large' style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          </div>

          {/* Title + primary actions */}
          {data?.course?.archivedAt && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '0.6em 1em', marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <Icon name='archive' />
              <span><strong>This class is archived.</strong> Future sessions have been cancelled. Past attendance records are preserved.</span>
            </div>
          )}
          {!data?.course?.archivedAt && (() => {
            const sessions = data?.course?.sessions ?? [];
            const activeSessions = sessions.filter((s: any) => s.reasonForCancellation == null);
            const isHistoric = activeSessions.length === 0 || activeSessions.every((s: any) => Number(s.startTime) < Date.now());
            return isHistoric ? (
              <div style={{ background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '4px', padding: '0.6em 1em', marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                <Icon name='history' />
                <span><strong>This class is historic.</strong> All sessions have passed.</span>
              </div>
            ) : null;
          })()}
          <div className={styles.titleRow}>
            {editMode && isAdmin ? (
              <CourseNameField
                value={newCourseName}
                sessions={data?.course?.sessions}
                onChange={(name: string) => {
                  setNewCourseName(name)
                  updateCourseName(name)
                }}
                // Accepting a suggestion, or a tidy on blur, saves straight away rather than
                // waiting on the throttle.
                onCommit={(name: string) => {
                  setNewCourseName(name)
                  saveCourseName(name)
                }}
              />
            ) : (
              <span className={styles.title}>{data?.course?.name}</span>
            )}

            {isAdmin && (
              <div className={styles.actions}>
                <Button
                  onClick={() => {
                    setEditMode(!editMode)
                    setNewCourseName(data && data.course && data.course.name)
                  }}
                >
                  <Icon name='edit' /> {editMode ? "Done editing" : "Edit"}
                </Button>
                <Button onClick={() => navigate(`${PATHS.CLASSES}/${id}/records`)}>
                  <Icon name='file alternate' /> Records
                </Button>
              </div>
            )}
          </div>

          {/* Edit-mode admin panel: duplicate + delete */}
          {isAdmin && editMode && (
            <Segment secondary className={styles.adminPanel}>
              {nextBlockTagId != null ? (
                <>
                  <Header as='h4'>Duplicate class for next block</Header>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>New class name</label>
                    <CourseNameField
                      value={newCourseNameDuplicate}
                      onChange={setNewCourseNameDuplicate}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Days to copy forward</label>
                    <Input
                      fluid
                      placeholder='Days to copy it forward'
                      value={daysToCopyItForward}
                      onChange={(e: any) => {
                        const value = Number(e.target.value)
                        if (value < 0 || isNaN(value)) {
                          return
                        }
                        setDaysToCopyItForward(Number(e.target.value))
                      }}
                    />
                  </div>
                  <p className={styles.muted}>
                    First class: {data && data.course && data.course.sessions.length > 0 && formatDateTime(Number(data.course.sessions[0].startTime) + daysToCopyItForward * 24 * 60 * 60 * 1000)}
                  </p>
                  <Button
                    primary
                    disabled={disableDuplicate || !duplicateNameIsValid || daysToCopyItForward === 0}
                    onClick={async () => {
                      await duplicateClass({ variables: {
                        courseToCopyId: Number(id),
                        newCourseName: newCourseNameDuplicate,
                        daysToCopyItForward,
                        newBlockTagId: nextBlockTagId,
                        oldBlockTagId: currentBlockTagId,
                      }});
                      setDisableDuplicate(true)
                    }}
                  >
                    Duplicate for next block
                  </Button>
                </>
              ) : (
                <p className={styles.muted}>The next block has not been created yet.</p>
              )}

              <Divider />

              <Button
                color='orange'
                basic
                icon='archive'
                content={data?.course?.archivedAt ? 'Already archived' : 'Archive class'}
                disabled={!!data?.course?.archivedAt}
                onClick={async () => {
                  if (window.confirm(
                    "Archive this class?\n\n" +
                    "This will:\n" +
                    "• Cancel all future sessions\n" +
                    "• Hide the class from teacher views\n\n" +
                    "Attendance records from sessions that have already run will be kept.\n\n" +
                    "Press OK to archive, or Cancel to go back."
                  )) {
                    await archiveCourse({ variables: { courseId: Number(id) } });
                    refetch({ variables: { courseId: Number(id) } });
                  }
                }}
              />

              <Divider />

              <Button
                color='teal'
                basic
                icon='user times'
                content='Remove future teacher attendance'
                onClick={async () => {
                  if (window.confirm(
                    "Remove teacher attendance from all future sessions of this class?\n\n" +
                    "Teacher attendance for sessions that have already run is kept.\n\n" +
                    "Press OK to remove, or Cancel to go back."
                  )) {
                    await removeFutureTeacherAttendance({ variables: { courseId: Number(id) } });
                    refetch({ variables: { courseId: Number(id) } });
                  }
                }}
              />

              <Button
                color='teal'
                basic
                icon='user plus'
                content='Add teacher attendance to future classes'
                onClick={async () => {
                  if (window.confirm(
                    "Add teacher attendance to all future sessions of this class?\n\n" +
                    "This adds the class's teachers to every future session, skipping any session that already has an entry for them.\n\n" +
                    "Press OK to add, or Cancel to go back."
                  )) {
                    await addFutureTeacherAttendance({ variables: { courseId: Number(id) } });
                    refetch({ variables: { courseId: Number(id) } });
                  }
                }}
              />

              <Button
                color='red'
                basic
                icon='trash'
                content='Delete class'
                onClick={async () => {
                  if (window.confirm(
                    "⚠️ Permanently delete this class?\n\n" +
                    "This will remove ALL records from the database — sessions, attendance history, everything. This cannot be undone.\n\n" +
                    "Only use this if the class was created by mistake. If the class ran but is now finished, use Archive instead.\n\n" +
                    "Press OK to permanently delete, or Cancel to go back."
                  )) {
                    await deleteCourse({ variables: { courseId: Number(id) }});
                    navigate(-1)
                  }
                }}
              />
            </Segment>
          )}
          <Divider horizontal>
            <Header as='h4'>
              Info
            </Header>
          </Divider>
          <AttendanceGraph data={data} />

          <div className={styles.infoGrid}>
            <span className={styles.infoLabel}>SKU</span>
            <span className={styles.infoValue}>{data && data.course && data.course.SKU}</span>

            <span className={styles.infoLabel}>Teachers</span>
            <span className={styles.infoValue}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5em' }}>
                {data?.course?.teachers?.map((o: any) => (
                  <Label key={o.id} image basic style={{ margin: 0 }}>
                    <Link to={`${PATHS.PEOPLE}/${o.id}`}>{o.name}</Link>
                    {data?.course?.canEdit && (
                      <Icon
                        name="delete"
                        title={`Remove ${o.name} from this class`}
                        onClick={() => {
                          if (teacherBusy) return;
                          // Worth confirming: this also clears their attendance rows for
                          // every future session of the class.
                          if (window.confirm(
                            `Remove ${o.name} as a teacher of this class?\n\n` +
                            'They lose access to the class and its students, and are taken off ' +
                            'the register for future sessions.\n\n' +
                            'Past sessions keep their record, and the class will still show that ' +
                            'they taught it.'
                          )) {
                            changeTeacher(o.id, false);
                          }
                        }}
                      />
                    )}
                  </Label>
                ))}

                {(data?.course?.teachers ?? []).length === 0 && (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>No teachers</span>
                )}

                {data?.course?.canEdit && (
                  <Dropdown
                    selectOnBlur={false}
                    value=""
                    loading={teacherBusy}
                    disabled={teacherBusy}
                    text="Add teacher"
                    options={(teacherCandidateData?.teacherCandidates ?? [])
                      .filter((c: any) => !(data?.course?.teachers ?? []).some((t: any) => t.id === c.id))
                      .map((c: any) => ({ key: c.id, value: c.id, text: c.name }))}
                    onChange={(_: any, { value }: any) => changeTeacher(Number(value), true)}
                    selection
                    search
                    style={{ minWidth: '12em' }}
                  />
                )}
              </div>

              {teacherError && <Message error size="tiny" content={teacherError} style={{ marginTop: '0.5em' }} />}
            </span>

            <span className={styles.infoLabel}>Attendance</span>
            <span className={styles.infoValue}>{data && data.course && Math.round(data.course.attendancePercentage * 100)}%</span>

            <span className={styles.infoLabel}>Participants</span>
            <span className={styles.infoValue}>{data && data.course && data.course.courseParticipantCount}</span>

            <span className={styles.infoLabel}>Tags</span>
            <span className={styles.infoValue}>
              <CourseTags tags={data && data.course && data.course.tags} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
            </span>
          </div>
          {editMode && <CourseAdmin participants={(data && data.course && data.course.futureParticipants) || []} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />}
          <Divider horizontal>
            <Header as='h4'>
              Sessions
            </Header>
          </Divider>
          {loading && (<Dimmer active inverted>
              <Loader inverted content='Loading' />
          </Dimmer>)
          }
          <Accordion style={{ marginBottom: '3em' }}>
            {!loading && !error && data != null && data.course && data.course.sessions.map((s: any) => {

              if (!editMode && s.reasonForCancellation != null && s.reasonForCancellation === 'Other') {
                return null; // Skip sessions that are cancelled with 'Other' reason
              }

              const inFuture = s.startTime > Number(new Date())

              const adjustTime = (magnitude: number, forwardDirection: boolean, unit: string) => async (e: any) => {
                e.stopPropagation()
                await updateSessionTime({ variables: { sessionId: s.id, magnitude, forwardDirection, unit } });
                refetch({ variables: { courseId: Number(id) } })
              }

              return (
              <Fragment key={s.id}>
                <Accordion.Title
                  style={s.startTime > TenHoursAgo ? {}: { opacity: "0.51"}}
                  active={activeId === s.id}
                  index={0}
                  onClick={() => {
                    if (s.id !== activeId) {
                      setIdentifier('');
                      setActiveId(s.id)
                    } else {
                      setActiveId(undefined)
                    }
                    setHasUsedAccordian(true)
                  }}
                >
                {s.reasonForCancellation != null ? (
                  <b>Class cancelled on {formatDateTimeToDay(s.startTime)}</b>
                ) : (
                  <div className={styles.sessionTitle}>
                    <span className={styles.sessionMeta}>
                      <Icon name='dropdown' />
                      <Icon name='user' /> {s.attendanceCount}
                    </span>
                    <b>{formatDateTime(s.startTime)}</b>
                    {!inFuture ? `(attendance: ${Math.round(s.attendancePercentage*100)}%)` : ""}
                    {isAdmin && editMode && (
                      <span className={styles.sessionControls}>
                        <Button size='mini' basic color='red' onClick={async (e:any) => {
                          e.stopPropagation()
                          await deleteSession({ variables: { sessionId: s.id, reason: 'Teacher sick'} })
                          refetch({ variables: { courseId: Number(id) } })
                        }}>Cancel session</Button>
                        <Button.Group size='mini' basic>
                          <Button onClick={adjustTime(1, false, "days")}>-1d</Button>
                          <Button onClick={adjustTime(1, false, "hours")}>-1h</Button>
                          <Button onClick={adjustTime(15, true, "minutes")}>+15m</Button>
                          <Button onClick={adjustTime(1, true, "hours")}>+1h</Button>
                          <Button onClick={adjustTime(1, true, "days")}>+1d</Button>
                        </Button.Group>
                      </span>
                    )}
                  </div>
                )}
                </Accordion.Title>
                {s.reasonForCancellation != null ? (
                  isAdmin && editMode ? (
                    <Dropdown
                        placeholder='Reason...'
                        selection
                        value={s.reasonForCancellation}
                        onChange={async (_, d) =>  {
                          await deleteSession({ variables: { sessionId: s.id, reason: d.value} })
                          setTimeout(() => refetch({ courseId: Number(id) }), 2000)
                        }}
                        options={CANCELLATION_REASON}
                    />
                  ) : (
                    <div className={styles.muted}>Reason: {s.reasonForCancellation}</div>
                  )
                ) : (
                  <Accordion.Content active={activeId === s.id}>
                    <Session canEditCourse={data && data.course && data.course.canEdit} session={s as any} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
                    {isAdmin && editMode && (
                      <div className={styles.addPersonRow}>
                        <Input
                            placeholder='StudentId to add'
                            value={identifier.toLocaleUpperCase()}
                            onKeyPress={async (e: any) => {
                               if (e.key === 'Enter') {
                                  await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                                  refetch({ variables: { courseId: Number(id) } })
                                  setIdentifier('')
                               }
                            }}
                            onChange={(e: any) => setIdentifier(e.target.value)}
                        />
                        <Button onClick={async () => {
                          await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                          refetch({ variables: { courseId: Number(id) } })
                          setIdentifier('')
                        }}>Add</Button>
                        <Button onClick={async () => {
                          await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PRESENT' } })
                          refetch({ variables: { courseId: Number(id) } })
                          setIdentifier('')
                        }}>Add as Present</Button>
                      </div>
                    )}
                  </Accordion.Content>
                )}
              </Fragment>
            )
            })
          }
          </Accordion>
            {isAdmin && editMode && (
              <div className={styles.addSessionRow}>
                <DatePicker
                    selected={newSessionDate}
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => {
                        if (date == null) return;
                        setNewSessionDate(date);
                    }}
                    showYearDropdown
                    maxDate={new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())}
                />
                <Button
                  onClick={async () => {
                    await addSessionToCourse({ variables: { courseId: Number(id), date: newSessionDate?.toISOString() } })
                    // move new session date forward 7 days
                    if (newSessionDate == null) return;
                    const newDate = new Date(newSessionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                    setNewSessionDate(newDate);
                    refetch({ variables: { courseId: Number(id) } })
                  }}
                >
                  Add session
                </Button>
              </div>
            )}
        </Container>
      </Segment>

    </ResponsiveContainer>
  );
}

export { AdminClassView }
